import { useEffect, useState } from "react";
import { Section } from "@/components/CalcUI";
import { fetchReferral, applyReferral } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gift, Copy, Lock, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
  const { isAuthed, setShowAuth } = useAuth();
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!isAuthed) return;
    try {
      setInfo(await fetchReferral());
    } catch (e) {
      console.error("Failed to load referral info", e);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  if (!isAuthed) {
    return (
      <div data-testid="referrals-page">
        <Section title="Refer a Rig Mate">
          <div className="border border-[#334155] bg-[#11151c] p-8 text-center">
            <Lock className="w-8 h-8 text-[#f59e0b] mx-auto mb-3" />
            <p className="mono text-sm text-[#94a3b8] mb-4">Sign in to grab your referral link.</p>
            <button
              data-testid="ref-signin-btn"
              onClick={() => setShowAuth(true)}
              className="bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-4 py-2 mono text-xs tracking-widest uppercase font-bold"
            >
              Sign in
            </button>
          </div>
        </Section>
      </div>
    );
  }

  const link = info?.referral_code
    ? `${window.location.origin}/pricing?ref=${info.referral_code}`
    : "";

  const share = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "RIGCALC", text: "The field-ready downhole formulas app. Use my link for $10 off:", url: link });
      } catch (e) {
        if (e?.name !== "AbortError") console.error("Share failed", e);
      }
    } else {
      navigator.clipboard.writeText(link);
      toast.success("Referral link copied");
    }
  };

  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await applyReferral(code.trim());
      toast.success("Referral applied · $10 credit locked in on your first paid plan");
      setCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="referrals-page">
      <Section title="Refer a Rig Mate" subtitle="Give $10, get $10. When someone you refer pays for Pro, Team, or Lifetime — you both get $10 in credit on your account.">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="border border-[#334155] bg-[#11151c] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-4 h-4 text-[#f59e0b]" />
              <h3 className="heading text-sm font-bold uppercase tracking-wide">Your Referral</h3>
            </div>
            <div className="text-xs mono tracking-widest text-[#94a3b8] uppercase mb-1">Your code</div>
            <div className="flex items-center gap-2 border border-[#334155] bg-[#0b0e13] px-3 py-2 mb-4">
              <span data-testid="my-ref-code" className="mono text-2xl text-[#f59e0b] font-bold tracking-widest">
                {info?.referral_code || "—"}
              </span>
              <button
                data-testid="copy-ref-btn"
                onClick={() => {
                  navigator.clipboard.writeText(info?.referral_code || "");
                  toast.success("Code copied");
                }}
                className="ml-auto text-[#94a3b8] hover:text-[#f59e0b]"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>

            <div className="text-xs mono tracking-widest text-[#94a3b8] uppercase mb-1">Share link</div>
            <div className="flex items-center gap-2 border border-[#334155] bg-[#0b0e13] px-3 py-2 mb-4">
              <span className="mono text-xs text-[#f8fafc] truncate flex-1">{link}</span>
              <button
                data-testid="share-ref-btn"
                onClick={share}
                className="text-[#94a3b8] hover:text-[#f59e0b] shrink-0"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="border border-[#334155] p-3 bg-[#0b0e13]">
                <div className="text-[10px] mono tracking-widest text-[#94a3b8] uppercase">Credit balance</div>
                <div className="mono text-xl text-[#10b981] font-bold" data-testid="credit-balance">
                  ${info?.credit_usd?.toFixed(2) ?? "0.00"}
                </div>
              </div>
              <div className="border border-[#334155] p-3 bg-[#0b0e13]">
                <div className="text-[10px] mono tracking-widest text-[#94a3b8] uppercase">Successful referrals</div>
                <div className="mono text-xl text-[#f59e0b] font-bold" data-testid="ref-count">
                  {info?.referral_count ?? 0}
                </div>
              </div>
            </div>
          </div>

          <div className="border border-[#334155] bg-[#11151c] p-6">
            <h3 className="heading text-sm font-bold uppercase tracking-wide mb-3">Redeem a Code</h3>
            <p className="text-sm text-[#94a3b8] mb-4">
              Enter a friend's referral code to lock in your $10 credit — applied automatically on your first paid plan.
            </p>
            {info?.referred_by ? (
              <div className="border-l-2 border-[#10b981] bg-[#10b981]/10 px-3 py-2 mono text-xs text-[#10b981]">
                Referral applied: <span className="font-bold">{info.referred_by}</span>. $10 credit will drop on your first paid plan.
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  data-testid="apply-ref-input"
                  placeholder="8-CHAR CODE"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="bg-[#0b0e13] border border-[#334155] focus:border-[#f59e0b] focus:outline-none px-3 py-2 mono text-sm uppercase flex-1"
                />
                <button
                  data-testid="apply-ref-btn"
                  onClick={apply}
                  disabled={busy}
                  className="bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-4 py-2 mono text-xs tracking-widest uppercase font-bold disabled:opacity-40"
                >
                  Apply
                </button>
              </div>
            )}
            <div className="mt-6 pt-6 border-t border-[#334155] text-xs mono text-[#64748b] leading-relaxed">
              How it works:
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>Send your link/code to a colleague.</li>
                <li>They sign up (7-day trial) and eventually pay for Pro/Team/Lifetime.</li>
                <li>Both accounts get $10 credit — applied to the next one-time checkout.</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
